import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { ArrowLeft, Building2, School, Users, Loader2, CheckCircle2, Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Super-admin: list all practices with PFP seat pricing configuration.
 * Option B (per-practice pricing): each school gets its own price/seat/cycle.
 * Amount is stored in cents (Paystack convention); UI displays in ZAR.
 */
export default function AdminPractices() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [editing, setEditing] = useState(null);          // practice row being edited
  const [priceRands, setPriceRands] = useState('');       // string in Rands
  const [saving, setSaving] = useState(false);
  // Phase 9.3 — create a PFP-only school with a school_admin
  const [creating, setCreating] = useState(false);
  const [newSchool, setNewSchool] = useState({ school_name: '', admin_email: '', admin_name: '', price_rands: '', seats_purchased: '', attach_existing_admin: false });
  const [createSubmit, setCreateSubmit] = useState(false);
  // Feb 2026 — rename / adjust seats / delete a school
  const [renaming, setRenaming] = useState(null);   // practice row
  const [renameForm, setRenameForm] = useState({ name: '', seats_purchased: '' });
  const [renameSubmit, setRenameSubmit] = useState(false);
  const [deleting, setDeleting] = useState(null);   // practice row
  const [deleteSubmit, setDeleteSubmit] = useState(false);

  useEffect(() => { fetchPractices(); }, []);

  const fetchPractices = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/admin/practices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(r.data.practices || []);
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error('Super-admin access required');
        navigate('/');
      } else {
        toast.error('Failed to load practices');
      }
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (row) => {
    setEditing(row);
    const cents = row.pfp_seats?.price_per_seat;
    setPriceRands(cents ? (cents / 100).toFixed(2) : '');
  };

  const savePrice = async () => {
    if (!editing) return;
    const raw = (priceRands || '').trim();
    let cents = null;
    if (raw !== '') {
      const rands = Number(raw);
      if (!Number.isFinite(rands) || rands < 0) {
        toast.error('Enter a valid amount (or leave blank to clear)');
        return;
      }
      cents = Math.round(rands * 100);
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/admin/practices/${editing.id}/pfp-price`,
        { price_per_seat: cents, currency: 'ZAR' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(cents ? `Set to R${(cents / 100).toFixed(2)} / seat / month` : 'Pricing cleared');
      setEditing(null);
      setPriceRands('');
      fetchPractices();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const filtered = filter.trim()
    ? rows.filter((r) => {
        const q = filter.toLowerCase();
        return (
          (r.name || '').toLowerCase().includes(q) ||
          (r.admin_email || '').toLowerCase().includes(q) ||
          (r.admin_name || '').toLowerCase().includes(q)
        );
      })
    : rows;

  const createPfpOnlySchool = async () => {
    const requireName = !newSchool.attach_existing_admin;
    if (!newSchool.school_name.trim() || !newSchool.admin_email.trim() || (requireName && !newSchool.admin_name.trim())) {
      return toast.error(requireName ? 'Fill school name, admin name and admin email' : 'Fill school name and admin email');
    }
    setCreateSubmit(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        school_name: newSchool.school_name.trim(),
        admin_email: newSchool.admin_email.trim().toLowerCase(),
        admin_name: newSchool.admin_name.trim() || newSchool.admin_email.split('@')[0],
        attach_existing_admin: !!newSchool.attach_existing_admin,
      };
      if (newSchool.price_rands) payload.price_per_seat = Math.round(Number(newSchool.price_rands) * 100);
      if (newSchool.seats_purchased) payload.seats_purchased = Math.max(0, parseInt(newSchool.seats_purchased, 10) || 0);
      const r = await axios.post(`${API}/admin/practices/pfp-only`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.data?.attached_existing) {
        toast.success(`${payload.admin_email} is now a school admin — they can log in with their existing password.`);
      } else {
        const url = r.data?.invite_url || '';
        try { await navigator.clipboard.writeText(url); } catch { /* silent */ }
        toast.success(r.data?.email_sent
          ? `School created — invite email sent to ${payload.admin_email}`
          : `School created. Invite link copied to clipboard (email not configured yet).`);
      }
      setCreating(false);
      setNewSchool({ school_name: '', admin_email: '', admin_name: '', price_rands: '', seats_purchased: '', attach_existing_admin: false });
      fetchPractices();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create school');
    } finally { setCreateSubmit(false); }
  };

  const openRename = (row) => {
    setRenaming(row);
    setRenameForm({
      name: row.name || '',
      seats_purchased: row.pfp_seats?.seats_purchased?.toString() || '',
    });
  };

  const saveRename = async () => {
    if (!renaming) return;
    const nameTrim = renameForm.name.trim();
    if (!nameTrim) return toast.error('School name is required');
    setRenameSubmit(true);
    try {
      const token = localStorage.getItem('token');
      const payload = { name: nameTrim };
      if (renameForm.seats_purchased !== '') {
        const n = parseInt(renameForm.seats_purchased, 10);
        if (!Number.isFinite(n) || n < 0) return toast.error('Seats must be 0 or positive');
        payload.seats_purchased = n;
      }
      await axios.patch(
        `${API}/admin/practices/${renaming.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('School updated');
      setRenaming(null);
      fetchPractices();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update');
    } finally {
      setRenameSubmit(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteSubmit(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/practices/${deleting.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Deleted ${deleting.name}`);
      setDeleting(null);
      fetchPractices();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not delete');
    } finally {
      setDeleteSubmit(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            data-testid="back-btn"
            onClick={() => navigate('/')}
            className="rounded-full"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h1 className="text-xl font-serif text-primary-900">Practices</h1>
              <p className="text-xs text-stone-500">Super-admin — PFP seat pricing (Option B)</p>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="mb-6">
            <h2 className="text-3xl md:text-4xl font-serif text-primary-900 mb-2">
              Practices with PFP Seat Pricing
            </h2>
            <p className="text-stone-600">
              Set per-practice PFP price/seat/month. Practice admins can only purchase
              seats after you configure a price here (Option B).
            </p>
          </div>

          <div className="mb-4 flex flex-col md:flex-row md:items-center gap-3">
            <Input
              placeholder="Search by practice name, admin email or name"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="h-12 rounded-xl max-w-md"
              data-testid="practices-filter"
            />
            <Button
              onClick={() => setCreating(true)}
              className="rounded-full h-12"
              data-testid="add-pfp-school-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Add PFP-only school
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
            </div>
          ) : filtered.length === 0 ? (
            <Card className="p-12 text-center rounded-3xl border border-stone-100">
              <School className="w-12 h-12 mx-auto mb-3 text-stone-300" />
              <p className="text-stone-500">No practices match your search.</p>
            </Card>
          ) : (
            <div className="grid gap-4">
              {filtered.map((p) => {
                const priceRands = p.pfp_seats?.price_per_seat
                  ? (p.pfp_seats.price_per_seat / 100).toFixed(2)
                  : null;
                return (
                  <Card
                    key={p.id}
                    className="p-5 rounded-3xl border border-stone-100 bg-white"
                    data-testid={`practice-card-${p.id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-lg font-medium text-primary-900 truncate">{p.name || '—'}</h3>
                          {p.pfp_seats?.active && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs rounded-full">
                              <CheckCircle2 className="w-3 h-3" /> PFP active
                            </span>
                          )}
                          {p.pfp_seats?.configured && !p.pfp_seats?.active && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 text-xs rounded-full">
                              Price set — awaiting purchase
                            </span>
                          )}
                          {!p.pfp_seats?.configured && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-stone-100 text-stone-600 text-xs rounded-full">
                              Not configured
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-stone-500">
                          {p.admin_name} · {p.admin_email}
                        </p>
                        <div className="flex flex-wrap gap-4 mt-2 text-sm text-stone-600">
                          <span className="inline-flex items-center gap-1">
                            <Users className="w-3.5 h-3.5" />
                            {p.therapist_count} therapist{p.therapist_count === 1 ? '' : 's'}
                          </span>
                          <span>
                            PFP price:{' '}
                            <strong data-testid={`practice-price-${p.id}`}>
                              {priceRands ? `R${priceRands} / seat / ${p.pfp_seats.billing_cycle}` : 'Not set'}
                            </strong>
                          </span>
                          {p.pfp_seats?.seats_purchased > 0 && (
                            <span>
                              {p.pfp_seats.seats_used} / {p.pfp_seats.seats_purchased} seats used
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col md:flex-row gap-2 md:items-start">
                        <Button
                          variant="outline"
                          className="rounded-full"
                          onClick={() => navigate(`/admin/practices/${p.id}/parents`)}
                          data-testid={`view-parents-${p.id}`}
                        >
                          View parents
                        </Button>
                        <Button
                          variant="outline"
                          className="rounded-full"
                          onClick={() => openEdit(p)}
                          data-testid={`set-pfp-price-${p.id}`}
                        >
                          {p.pfp_seats?.configured ? 'Update price' : 'Set PFP price'}
                        </Button>
                        <Button
                          variant="outline"
                          className="rounded-full"
                          onClick={() => openRename(p)}
                          data-testid={`edit-practice-${p.id}`}
                          title="Rename school or adjust seat cap"
                        >
                          <Pencil className="w-4 h-4 md:mr-2" />
                          <span className="hidden md:inline">Edit</span>
                        </Button>
                        <Button
                          variant="outline"
                          className="rounded-full text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                          onClick={() => setDeleting(p)}
                          data-testid={`delete-practice-${p.id}`}
                          title="Delete school"
                        >
                          <Trash2 className="w-4 h-4 md:mr-2" />
                          <span className="hidden md:inline">Delete</span>
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </motion.div>
      </main>

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) { setEditing(null); setPriceRands(''); } }}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="edit-price-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif">
              PFP price · {editing?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-stone-600">
              Per-seat monthly PFP price this practice will be charged. Leave blank to
              clear pricing (practice admin will see a &quot;contact support&quot; prompt).
            </p>
            <div className="space-y-2">
              <Label>Price per seat / month (ZAR)</Label>
              <div className="flex items-center gap-2">
                <span className="text-stone-500">R</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="e.g. 99.00"
                  value={priceRands}
                  onChange={(e) => setPriceRands(e.target.value)}
                  className="h-12 rounded-xl"
                  data-testid="edit-price-input"
                  autoFocus
                />
              </div>
              <p className="text-xs text-stone-400">
                Stored as {priceRands ? `${Math.round(Number(priceRands) * 100)} cents` : 'null (cleared)'}
              </p>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" className="rounded-full" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button
              className="rounded-full bg-purple-600 hover:bg-purple-700"
              onClick={savePrice}
              disabled={saving}
              data-testid="save-price-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add PFP-only school dialog (Phase 9.3) */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="rounded-3xl max-w-lg" data-testid="create-pfp-school-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif">Add a PFP-only school</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-stone-600">
              For schools that want PlayFirst Parent for families but don't use
              PlayFirst Clinical themselves. Creates a school + a school_admin
              user with a magic-link invite emailed to them.
            </p>

            <label className="flex items-start gap-3 p-3 rounded-2xl border border-stone-200 bg-stone-50 cursor-pointer">
              <input
                type="checkbox"
                checked={newSchool.attach_existing_admin}
                onChange={(e) => setNewSchool({ ...newSchool, attach_existing_admin: e.target.checked })}
                className="mt-1"
                data-testid="attach-existing-toggle"
              />
              <span className="text-sm">
                <strong>Attach an existing user</strong> — tick this if the admin
                is already signed up (e.g. a PFP parent who owns a school). Their
                current password stays; we skip the magic-link email.
              </span>
            </label>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Label>School name</Label>
                <Input
                  value={newSchool.school_name}
                  onChange={(e) => setNewSchool({ ...newSchool, school_name: e.target.value })}
                  className="h-12 rounded-xl mt-1"
                  placeholder="e.g. Bluebird Primary"
                  data-testid="new-school-name"
                  autoFocus
                />
              </div>
              <div>
                <Label>Admin name{newSchool.attach_existing_admin ? ' (optional — kept from existing user)' : ''}</Label>
                <Input
                  value={newSchool.admin_name}
                  onChange={(e) => setNewSchool({ ...newSchool, admin_name: e.target.value })}
                  className="h-12 rounded-xl mt-1"
                  placeholder="e.g. Sarah Ndlovu"
                  data-testid="new-school-admin-name"
                />
              </div>
              <div>
                <Label>Admin email</Label>
                <Input
                  type="email"
                  value={newSchool.admin_email}
                  onChange={(e) => setNewSchool({ ...newSchool, admin_email: e.target.value })}
                  className="h-12 rounded-xl mt-1"
                  placeholder="admin@school.edu"
                  data-testid="new-school-admin-email"
                />
              </div>
              <div>
                <Label>Price / seat / month (ZAR — optional)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newSchool.price_rands}
                  onChange={(e) => setNewSchool({ ...newSchool, price_rands: e.target.value })}
                  className="h-12 rounded-xl mt-1"
                  placeholder="e.g. 99.00"
                  data-testid="new-school-price"
                />
              </div>
              <div>
                <Label>Seats to seed (optional)</Label>
                <Input
                  type="number"
                  min="0"
                  value={newSchool.seats_purchased}
                  onChange={(e) => setNewSchool({ ...newSchool, seats_purchased: e.target.value })}
                  className="h-12 rounded-xl mt-1"
                  placeholder="e.g. 30"
                  data-testid="new-school-seats"
                />
              </div>
            </div>
            <p className="text-xs text-stone-400">
              Seeded seats aren't billed until you flip <code>pfp_seats.active</code>
              to true (via the school admin's Paystack checkout, or set manually).
            </p>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" className="rounded-full" onClick={() => setCreating(false)}>Cancel</Button>
            <Button
              className="rounded-full bg-emerald-600 hover:bg-emerald-700"
              onClick={createPfpOnlySchool}
              disabled={createSubmit}
              data-testid="create-school-submit"
            >
              {createSubmit ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Create school & send invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename / adjust seats dialog */}
      <Dialog open={!!renaming} onOpenChange={(v) => !v && setRenaming(null)}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="edit-practice-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif">Edit {renaming?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>School name</Label>
              <Input
                value={renameForm.name}
                onChange={(e) => setRenameForm({ ...renameForm, name: e.target.value })}
                className="h-12 rounded-xl"
                data-testid="edit-practice-name-input"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label>Total seats purchased (cap)</Label>
              <Input
                type="number"
                min="0"
                value={renameForm.seats_purchased}
                onChange={(e) => setRenameForm({ ...renameForm, seats_purchased: e.target.value })}
                className="h-12 rounded-xl"
                placeholder="e.g. 30"
                data-testid="edit-practice-seats-input"
              />
              <p className="text-xs text-stone-500">
                Current usage: {renaming?.pfp_seats?.seats_used ?? 0} / {renaming?.pfp_seats?.seats_purchased ?? 0}
              </p>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" className="rounded-full" onClick={() => setRenaming(null)}>Cancel</Button>
            <Button
              className="rounded-full bg-purple-600 hover:bg-purple-700"
              onClick={saveRename}
              disabled={renameSubmit}
              data-testid="edit-practice-save-btn"
            >
              {renameSubmit ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent data-testid="delete-practice-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleting?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the school and its pending school-admin invite. Any therapists
              linked to it or parents sponsored by it must be moved off first — the API
              will block this delete otherwise, so nothing gets orphaned by accident.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-practice-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={confirmDelete}
              disabled={deleteSubmit}
              data-testid="delete-practice-confirm"
            >
              {deleteSubmit ? 'Deleting…' : 'Yes, delete school'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
